// src/pages/BonusDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, collectionGroup, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { BONUS_LIST } from "../components/BonusList";

const getPointBadgeClass = (points) => {
    if (points > 0) return "positive";
    if (points < 0) return "negative";
    return "neutral";
};

function BonusDetails() {
    const { bonusValue } = useParams();
    const navigate = useNavigate();
    const baseBonusValue = String(bonusValue || "").endsWith("_ext")
        ? String(bonusValue).slice(0, -4)
        : bonusValue;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]); // [{ wrestlerId, count, wrestler }]
    const [minCount] = useState(1);

    const bonus = useMemo(() => {
        const found = BONUS_LIST.find((b) => b.value === baseBonusValue);
        return (
            found || {
                value: baseBonusValue,
                label: baseBonusValue,
                points: 0,
                description: "",
            }
        );
    }, [baseBonusValue]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError("");

            try {
                // 1) Carico tutte le partecipazioni (collectionGroup)
                const pSnap = await getDocs(collectionGroup(db, "participations"));
                const participations = pSnap.docs.map((d) => d.data());

                // 2) Conteggio quante volte compare bonusValue per ogni wrestlerId
                const counts = new Map(); // wrestlerId -> count

                for (const p of participations) {
                    if (!p?.wrestlerId) continue;
                    const bonuses = Array.isArray(p.bonuses) ? p.bonuses : [];
                    if (bonuses.length === 0) continue;

                    // conta occorrenze (se lo stesso bonus compare 2 volte nello stesso array, le conta entrambe)
                    const occurrences = bonuses.reduce(
                        (acc, b) => {
                            const storedBaseValue = String(b || "").endsWith("_ext")
                                ? String(b).slice(0, -4)
                                : b;
                            return acc + (storedBaseValue === baseBonusValue ? 1 : 0);
                        },
                        0
                    );

                    if (occurrences > 0) {
                        counts.set(p.wrestlerId, (counts.get(p.wrestlerId) || 0) + occurrences);
                    }
                }

                // 3) Carico tutti i wrestler per mostrare nome/immagine
                const wSnap = await getDocs(collection(db, "wrestlers"));
                const wrestlerMap = new Map(); // id -> wrestlerData
                wSnap.docs.forEach((d) => {
                    wrestlerMap.set(d.id, { id: d.id, ...d.data() });
                });

                // 4) Unisco (solo wrestler che hanno count > 0, esclusi gli ospiti)
                const merged = Array.from(counts.entries())
                    .filter(([wrestlerId]) => !wrestlerMap.get(wrestlerId)?.isGuest)
                    .map(([wrestlerId, count]) => {
                        const w = wrestlerMap.get(wrestlerId);
                        return {
                            wrestlerId,
                            count,
                            wrestler: w
                                ? {
                                    id: w.id,
                                    name: w.name || w.nome || "Sconosciuto",
                                    imageUrl: w.imageUrl || "",
                                    roster: w.roster || "",
                                    score: w.score || 0,
                                }
                                : {
                                    id: wrestlerId,
                                    name: "Wrestler non trovato",
                                    imageUrl: "",
                                    roster: "",
                                    score: 0,
                                },
                        };
                    })
                    .sort((a, b) => b.count - a.count || a.wrestler.name.localeCompare(b.wrestler.name));

                setRows(merged);
            } catch (e) {
                console.error(e);
                setError("Errore nel caricamento dei dati.");
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [baseBonusValue]);

    const filteredRows = useMemo(() => {
        const mc = Number(minCount) || 1;
        return rows.filter((r) => r.count >= mc);
    }, [rows, minCount]);

    const pointVariants = useMemo(() => {
        const variants = [];

        variants.push({
            key: "standard",
            label: "Standard",
            points: typeof bonus.points === "number" ? bonus.points : 0,
            isExt: false,
        });

        if (typeof bonus.pointsExt === "number") {
            variants.push({
                key: "extra",
                label: "Extra",
                points: bonus.pointsExt,
                isExt: true,
            });
        }

        return variants;
    }, [bonus]);

    return (
        <div className="bonus-details-page">
        <div className="container py-4">
            {/* Header con brand e badge bonus */}
            <div className="bonus-details-hero p-4 rounded-4 mb-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
                    <div className="d-flex gap-3 align-items-center">
                        <div>
                            <h1 className="h3 fw-bold text-brand-secondary-light mb-2">Dettaglio Bonus/Malus</h1>
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                <span
                                    className={`bonus-badge ${getPointBadgeClass(bonus.points)} ${pointVariants.length > 1 ? "bonus-badge--ext" : ""} fs-6 px-3 py-2`}
                                    title={pointVariants.map((variant) => `${variant.label}: ${variant.points > 0 ? "+" : ""}${variant.points}`).join(" | ")}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (bonus.value) {
                                            navigate(`/bonus/${encodeURIComponent(String(bonus.value).toLowerCase())}`);
                                        }
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    <span className="bonus-badge__label">{bonus.label}</span>
                                    <span className="bonus-badge__points">
                                        {pointVariants.map((variant) => `${variant.points > 0 ? "+" : ""}${variant.points}`).join(" | ")}
                                    </span>
                                </span>

                                {bonus.desc && (
                                    <span className="text-muted-brand small bonus-details-pill px-3 py-2 rounded-pill">
                                        {bonus.desc}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5 bonus-details-state rounded-3">
                    <div className="spinner-border text-brand-secondary" role="status" />
                    <div className="text-brand-secondary-light mt-2 fw-semibold">Caricamento...</div>
                </div>
            ) : error ? (
                <div className="alert border-0 border-start border-4 border-brand-accent bonus-details-error shadow-sm">
                    <i className="bi bi-exclamation-triangle-fill text-brand-accent me-2" />
                    {error}
                </div>
            ) : filteredRows.length === 0 ? (
                <div className="alert bonus-details-state border-0 rounded-3 py-4 text-center">
                    <i className="bi bi-emoji-frown fs-1 text-brand-secondary opacity-50 mb-2 d-block" />
                    <span className="fw-semibold">Nessun wrestler ha questo bonus</span>
                </div>
            ) : (
                <div className="card border-0 shadow-sm bonus-details-card">
                    <div className="card-body p-0">
                        <div className="table-responsive bonus-details-table-wrapper">
                            <table className="table table-hover table-dark mb-0 align-middle bonus-details-table">
                                <thead>
                                    <tr>
                                        <th className="py-3">Wrestler</th>
                                        <th className="py-3 text-end">N</th>
                                        <th className="py-3 text-end">Punteggio</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((r) => (
                                        <tr
                                            key={r.wrestlerId}
                                            className="bonus-details-row"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => navigate(`/wrestlers/${r.wrestlerId}`)}
                                        >
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {r.wrestler.imageUrl ? (
                                                        <img
                                                            src={r.wrestler.imageUrl}
                                                            alt={r.wrestler.name}
                                                            className="rounded bg-brand-dark"
                                                            style={{ width: 44, height: 44, objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="bg-secondary rounded d-flex align-items-center justify-content-center"
                                                            style={{ width: 44, height: 44 }}
                                                        >
                                                            <i className="bi bi-person text-white" />
                                                        </div>
                                                    )}
                                                    <div className="fw-semibold text-truncate" style={{ maxWidth: 260 }}>
                                                        {r.wrestler.name}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-end fw-bold text-brand-secondary-light">{r.count}</td>
                                            <td className="text-end text-light">{r.wrestler.score ?? 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-4 d-flex flex-wrap gap-3 justify-content-between">
                <div className="justify-content-start">
                    <Link to="/wrestlers" className="btn-back">
                        <i className="bi bi-arrow-left me-2" />
                        Elenco wrestler
                    </Link>
                </div>
                <div className="justify-content-end">
                    <Link to="/stats" className="btn-back">
                        Statistiche
                        <i className="bi bi-arrow-right me-2" />
                    </Link>
                </div>
            </div>
        </div>
        </div>
    );
}

export default BonusDetails;
